import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { GetAccountListProps } from "@/services/accounts.service";
import { getPPFilterRequest, PPFRProps } from "./filter-obj";
import { formatCurrencyInputToNumber } from "@/utils/query-string";
import {
  createUpdatePrices,
  CreateUpdatePricesProps,
  getBoardSection,
  getDollarSection,
  getGFSection,
  getPricesSection,
  getSuppliesSection,
} from "@/services/price-board.service";
import { toaster } from "@/components/ui/toaster";
import { NavigateFunction } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { AxiosError } from "axios";
import { GeneralFilterData, PriceBoardFormValues } from "./price-board.schema";
import { handlePermissionError } from "@/utils/permission-check";
import { isEmpty } from "lodash";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues, section: string) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  currentSection: string;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
}

export const clearFiltersHandler = async ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
  setCurrentFilters,
  currentSection,
}: ClearFiltersHandlerProps) => {
  // Limpiar formulario
  setValue("dateFrom", undefined);
  setValue("dateTo", undefined);
  setValue("generalDate", undefined);

  setCurrentFilters({});
  setFiltersApplied(false);

  await submitFilters(getValues(), currentSection);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters?: Dispatch<SetStateAction<{}>>;
  fetchSection: (currentSection: string, props: any) => Promise<void>;
  event: FieldValues;
  currentSection?: string;
}

export const submitFiltersHandlerPricesSection = async ({
  event,
  currentSection,
  setCurrentFilters,
  setFiltersApplied,
  fetchSection,
  setOpenFilters,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj: Partial<GetAccountListProps> = getPPFilterRequest(
    event as PPFRProps,
  );

  setCurrentFilters?.(filterObj);

  if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

  await fetchSection(currentSection!, filterObj);

  setOpenFilters(false);
};

interface FillPriceValuesProps {
  gfData?: GeneralFilterData;
  setValue: UseFormSetValue<FieldValues>;
}

export const fillPriceValues = ({ gfData, setValue }: FillPriceValuesProps) => {
  if (!gfData) return;

  const setGroupValues = (
    key: string,
    data:
      | {
          precioSoja?: any;
          precioMaiz?: any;
          precioTrigo?: any;
          fecha?: any;
        }[]
      | undefined,
  ) => {
    if (data?.length) {
      const item = data[0];
      if (item.fecha) setValue("fechaPizarra", item.fecha); // caso especial
      setValue(
        `soja${key}` as keyof PriceBoardFormValues,
        item.precioSoja ?? "",
      );
      setValue(
        `maiz${key}` as keyof PriceBoardFormValues,
        item.precioMaiz ?? "",
      );
      setValue(
        `trigo${key}` as keyof PriceBoardFormValues,
        item.precioTrigo ?? "",
      );
    } else {
      setValue(`soja${key}` as keyof PriceBoardFormValues, "");
      setValue(`maiz${key}` as keyof PriceBoardFormValues, "");
      setValue(`trigo${key}` as keyof PriceBoardFormValues, "");
      if (key === "Pizarra") setValue("fechaPizarra", "");
    }
  };

  setGroupValues("Mercado", gfData.preciosMercado);
  setGroupValues("Union", gfData.preciosUnion);
  setGroupValues("Insumos", gfData.insumos);
  setGroupValues("Pizarra", gfData.pizarra);
};

interface CreateUpdatePricesHandlerProps {
  setLoadingCreateUpdatePrices: Dispatch<SetStateAction<boolean>>;
  getValues: UseFormGetValues<FieldValues>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export const createUpdatePricesHandler = async ({
  getValues,
  setLoadingCreateUpdatePrices,
  navigate,
  fetchPermissions,
  setOpen,
}: CreateUpdatePricesHandlerProps) => {
  setLoadingCreateUpdatePrices(true);
  try {
    const currentValues = getValues();

    const objToSend: CreateUpdatePricesProps = {
      fecha: currentValues?.fecha,

      // Obligatorios
      sojaMercado: formatCurrencyInputToNumber(currentValues?.sojaMercado),
      maizMercado: formatCurrencyInputToNumber(currentValues?.maizMercado),
      trigoMercado: formatCurrencyInputToNumber(currentValues?.trigoMercado),

      sojaUnion: formatCurrencyInputToNumber(currentValues?.sojaUnion),
      maizUnion: formatCurrencyInputToNumber(currentValues?.maizUnion),
      trigoUnion: formatCurrencyInputToNumber(currentValues?.trigoUnion),

      //Opcionales
      ...(currentValues?.sojaInsumos
        ? {
            sojaInsumos: formatCurrencyInputToNumber(currentValues.sojaInsumos),
          }
        : {}),
      ...(currentValues?.maizInsumos
        ? {
            maizInsumos: formatCurrencyInputToNumber(currentValues.maizInsumos),
          }
        : {}),
      ...(currentValues?.trigoInsumos
        ? {
            trigoInsumos: formatCurrencyInputToNumber(
              currentValues.trigoInsumos,
            ),
          }
        : {}),

      ...(currentValues?.sojaPizarra
        ? {
            sojaPizarra: formatCurrencyInputToNumber(currentValues.sojaPizarra),
          }
        : {}),
      ...(currentValues?.maizPizarra
        ? {
            maizPizarra: formatCurrencyInputToNumber(currentValues.maizPizarra),
          }
        : {}),
      ...(currentValues?.trigoPizarra
        ? {
            trigoPizarra: formatCurrencyInputToNumber(
              currentValues.trigoPizarra,
            ),
          }
        : {}),
    };

    await createUpdatePrices(objToSend);

    toaster.create({
      type: "success",
      title: "Precios agregados exitosamente.",
    });

    navigate(AppRoutes.PRICE_BOARD);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRICE_BOARD,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo agregar los precios en la fecha seleccionada.",
      });
    }
    throw error;
  } finally {
    setLoadingCreateUpdatePrices(false);
    setOpen(false);
  }
};

interface FetchSectionHandlerProps {
  setLoadingPrices: Dispatch<SetStateAction<boolean>>;
  setPricesData: Dispatch<SetStateAction<any>>;
  setSuppliesData: Dispatch<SetStateAction<any>>;
  setDollarData: Dispatch<SetStateAction<any>>;
  setBoardData: Dispatch<SetStateAction<any>>;
  filters?: any;
  section: string;
  showLoad?: boolean;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchSectionHandler = async ({
  setLoadingPrices,
  setPricesData,
  setSuppliesData,
  setDollarData,
  setBoardData,
  filters,
  section,
  showLoad = true,
  navigate,
  fetchPermissions,
}: FetchSectionHandlerProps) => {
      try {
        if (showLoad) setLoadingPrices(true);
        switch (section) {
          case "Precios":
            setPricesData(await getPricesSection(filters));
            break;
          case "Insumos":
            setSuppliesData(await getSuppliesSection(filters));
            break;
          case "Dolar Divisa":
            setDollarData(await getDollarSection(filters));
            break;
          case "Pizarra":
            setBoardData(await getBoardSection(filters));
            break;
        }
      } catch (error) {
        const handled = await handlePermissionError(
          error,
          navigate,
          fetchPermissions,
        );
        if (!handled) {
          toaster.create({
            type: "error",
            title: "No se pudo obtener la información.",
          });
        }
      } finally {
        setLoadingPrices(false);
      }
};

interface FetchGFSectionHandlerProps {
  setLoadingPrices: Dispatch<SetStateAction<boolean>>;
  setGFData: Dispatch<SetStateAction<any>>;
  filterDate: string;
  showLoad?: boolean;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchGFSectionHandler = async ({
  setLoadingPrices,
  setGFData,
  filterDate,
  showLoad = true,
  navigate,
  fetchPermissions,
}: FetchGFSectionHandlerProps) => {
  try {
    if (isEmpty(filterDate)) return
    if (showLoad) setLoadingPrices(true);
    setGFData(
      await getGFSection({
        fecha: filterDate,
      }),
    );
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRICE_BOARD,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo obtener la información.",
      });
    }
    throw error;
  } finally {
    setLoadingPrices(false);
  }
};
